import type { StatusIconBarItem } from '@chatsdet/ui';
import type { WorkflowTestStreamState } from '../../hooks/useWorkflowTestStream';
import type { WorkflowTestStatus } from '@chatsdet/types';
import type { WorkflowTestExecutionDto } from './useWorkflowTestExecutions';
import type { AgenticTestRunDto } from './useAgenticRunDetail';
import { latestWorkflowTestRunAttempts } from '@chatsdet/types';

export type RunSummaryData = {
  executionId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number;
  totalWorkflows: number;
  completedWorkflows: number;
  successfulWorkflows: number;
  failedWorkflows: number;
  skippedWorkflows: number;
  quarantinedWorkflows: number;
  degradedWorkflows: number;
  detailRowsUnavailable: boolean;
  items: StatusIconBarItem[];
};

type WorkflowListItem = {
  id: string;
  name: string;
};

const liveWallClockStatuses = new Set<WorkflowTestStatus>(['running', 'waiting']);

/**
 * Normalize live SSE stream data into RunSummaryData.
 */
export function normalizeFromStream(
  executionId: string,
  executionStatus: 'idle' | 'running' | 'completed',
  executionDurationMs: number,
  executionState: WorkflowTestStreamState,
  workflows: WorkflowListItem[]
): RunSummaryData {
  const items: StatusIconBarItem[] = workflows.map((workflow) => {
    const test = executionState.tests[workflow.id];
    const status: WorkflowTestStatus = test?.status ?? 'queued';
    const startedAtTs = test?.startedAt ? new Date(test.startedAt).getTime() : null;
    const finishedAtTs = test?.finishedAt ? new Date(test.finishedAt).getTime() : null;
    let durationMs = test?.durationMs ?? null;
    if (liveWallClockStatuses.has(status) && startedAtTs) {
      durationMs = Date.now() - startedAtTs;
    } else if (!durationMs && startedAtTs && finishedAtTs) {
      durationMs = finishedAtTs - startedAtTs;
    }

    return {
      // Use the live testRunId as the square's id. Falls back to workflow.id
      // only for workflows that haven't produced a test entry yet (e.g. queued
      // with no run created). workflowRunId, when present, lets the click link
      // straight to /runs/$runId instead of going through the resolver.
      id: test?.testRunId ?? workflow.id,
      workflowRunId: test?.workflowRunId ?? null,
      name: workflow.name || 'Untitled workflow',
      status,
      startedAt: test?.startedAt ?? null,
      finishedAt: test?.finishedAt ?? null,
      durationMs,
      errorMessage: test?.errorMessage ?? null,
      message: test?.message ?? null,
      linkedIssueId: test?.linkedIssueId ?? null,
      isQuarantined: test?.isQuarantined ?? false,
      skipReason: test?.skipReason ?? null,
      resumeAt: test?.resumeAt ?? null,
      segmentIndex: test?.segmentIndex ?? null,
      totalSegments: test?.totalSegments ?? null,
    };
  });

  const total = executionState.totalWorkflows || workflows.length || items.length || 0;
  const completed =
    executionState.completedWorkflows ||
    items.filter(
      (item) =>
        item.status === 'success' ||
        item.status === 'failed' ||
        item.status === 'skipped' ||
        item.status === 'flaky_success'
    ).length;

  return {
    executionId,
    status: executionStatus,
    startedAt: executionState.startedAt ?? null,
    finishedAt: executionState.finishedAt ?? null,
    durationMs: executionDurationMs,
    totalWorkflows: total,
    completedWorkflows: completed,
    successfulWorkflows: executionState.successfulWorkflows,
    failedWorkflows: executionState.failedWorkflows,
    skippedWorkflows: executionState.skippedWorkflows,
    quarantinedWorkflows: executionState.quarantinedWorkflows,
    degradedWorkflows: executionState.degradedWorkflows,
    detailRowsUnavailable: false,
    items,
  };
}

/**
 * Normalize REST detail-page data into RunSummaryData.
 */
export function normalizeFromRest(
  execution: WorkflowTestExecutionDto,
  testRuns: AgenticTestRunDto[]
): RunSummaryData {
  // The API normally returns latest attempts only. Keep the shared reducer at
  // this boundary as defense-in-depth for cached or mixed-version responses.
  const dedupedRuns = latestWorkflowTestRunAttempts(testRuns);
  const items: StatusIconBarItem[] = dedupedRuns.map((run) => ({
    id: run.id,
    workflowRunId: run.workflowRunId,
    name: run.workflowName ?? 'Unnamed Flow',
    status: run.status as WorkflowTestStatus,
    startedAt: run.startedAt,
    finishedAt: run.finishedAt,
    durationMs: run.startedAt
      ? run.finishedAt
        ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
        : Date.now() - new Date(run.startedAt).getTime()
      : null,
    errorMessage: run.errorMessage,
    message: null,
    linkedIssueId: run.linkedIssueId,
    isQuarantined: run.isQuarantined ?? false,
    skipReason: run.skipReason,
  }));

  const durationMs = execution.startedAt
    ? execution.finishedAt
      ? new Date(execution.finishedAt).getTime() - new Date(execution.startedAt).getTime()
      : Date.now() - new Date(execution.startedAt).getTime()
    : 0;

  return {
    executionId: execution.id,
    status: execution.status,
    startedAt: execution.startedAt,
    finishedAt: execution.finishedAt,
    durationMs,
    totalWorkflows: execution.totalWorkflows,
    completedWorkflows: execution.completedWorkflows,
    successfulWorkflows: execution.successfulWorkflows,
    failedWorkflows: execution.failedWorkflows,
    skippedWorkflows: execution.skippedWorkflows,
    quarantinedWorkflows: execution.quarantinedWorkflows,
    degradedWorkflows: execution.degradedWorkflows ?? 0,
    detailRowsUnavailable: items.length === 0 && execution.totalWorkflows > 0,
    items,
  };
}
